import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ShoppingBag, Truck, AlertTriangle, Euro, Search, RefreshCw, Loader2, X,
  Send, CheckCircle2, Ban, PlayCircle, ChevronRight, Package, MapPin, Mail, Clock,
} from "lucide-react";
import AdminShell from "./AdminShell";
import {
  ordersList, ordersStats, orderDetail, orderResend, orderMarkDelivered,
  orderCancel, orderNote, orderAdvance, ordersRefreshTracking, orderSimulate, errMessage,
} from "../../lib/api";
import { eur } from "../../config";

const STATUS = {
  PENDING: { label: "En attente paiement", cls: "bg-gray-100 text-ardoise" },
  PAID: { label: "Payée", cls: "bg-blue-50 text-blue-700" },
  SENT_TO_CJ: { label: "Transmise à CJ", cls: "bg-indigo-50 text-indigo-700" },
  PROCESSING: { label: "En préparation", cls: "bg-amber-50 text-amber-700" },
  SHIPPED: { label: "Expédiée", cls: "bg-cyan-50 text-cyan-700" },
  IN_TRANSIT: { label: "En transit", cls: "bg-teal-50 text-teal-700" },
  DELIVERED: { label: "Livrée", cls: "bg-green-50 text-green-700" },
  ON_HOLD: { label: "En attente (problème)", cls: "bg-red-50 text-red-600" },
  CANCELLED: { label: "Annulée", cls: "bg-gray-100 text-ardoise/60" },
  REFUNDED: { label: "Remboursée", cls: "bg-rose/30 text-ardoise" },
};
const TIMELINE = ["PAID", "SENT_TO_CJ", "PROCESSING", "SHIPPED", "IN_TRANSIT", "DELIVERED"];
const fmt = (v) => (v ? new Date(v).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const Badge = ({ s }) => { const c = STATUS[s] || STATUS.PENDING; return <span className={`px-2 py-0.5 text-[11px] uppercase tracking-wider ${c.cls}`}>{c.label}</span>; };

function Stat({ label, value, Icon, tid }) {
  return (
    <div className="border border-dore/20 bg-white p-4" data-testid={tid}>
      <div className="flex items-center gap-2 text-ardoise/60 text-[11px] uppercase tracking-wider mb-1"><Icon size={13} className="text-dore" /> {label}</div>
      <div className="text-2xl font-display text-encre">{value}</div>
    </div>
  );
}

function OrderDetail({ id, onClose, onChange }) {
  const [o, setO] = useState(null);
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const load = useCallback(async () => { try { const d = await orderDetail(id); setO(d); setNote(d.admin_notes || ""); } catch (e) { toast.error(errMessage(e)); } }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, label) => {
    setBusy(label);
    try { await fn(); toast.success("Fait"); await load(); onChange && onChange(); }
    catch (e) { toast.error(errMessage(e)); }
    setBusy("");
  };

  if (!o) return null;
  const curIdx = TIMELINE.indexOf(o.status);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={onClose} data-testid="order-detail-modal">
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-noir text-white px-5 py-4 flex items-center justify-between">
          <div>
            <div className="font-display text-lg">{o.order_number}</div>
            <div className="text-xs text-white/60">{fmt(o.created_at)}</div>
          </div>
          <button onClick={onClose} data-testid="detail-close"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-5">
          <div className="flex items-center justify-between">
            <Badge s={o.status} />
            {o.is_guadeloupe && <span className="text-[11px] text-dore">📍 Guadeloupe (971)</span>}
          </div>

          {/* Timeline */}
          <div className="flex items-center justify-between" data-testid="order-timeline">
            {TIMELINE.map((st, i) => (
              <React.Fragment key={st}>
                <div className="flex flex-col items-center flex-1">
                  <div className={`w-3 h-3 rounded-full ${i <= curIdx ? "bg-dore" : "bg-gray-200"}`} />
                  <span className={`text-[9px] mt-1 text-center ${i <= curIdx ? "text-encre" : "text-ardoise/40"}`}>{STATUS[st].label.split(" ")[0]}</span>
                </div>
                {i < TIMELINE.length - 1 && <div className={`h-0.5 flex-1 ${i < curIdx ? "bg-dore" : "bg-gray-200"}`} />}
              </React.Fragment>
            ))}
          </div>

          {/* Tracking */}
          {o.tracking_number && (
            <div className="border border-dore/20 p-3 text-sm" data-testid="order-tracking">
              <div className="flex items-center gap-2 text-encre"><Truck size={15} className="text-dore" /> {o.shipping_carrier}</div>
              <div className="text-ardoise mt-1">Suivi : <b>{o.tracking_number}</b></div>
              {o.tracking_url && <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-dore text-xs underline">Suivre le colis →</a>}
            </div>
          )}

          {/* Client + livraison */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="border border-dore/10 p-3">
              <div className="text-[11px] uppercase tracking-wider text-ardoise/60 mb-1">Client</div>
              <div className="text-encre">{o.customer_name}</div>
              <div className="text-ardoise/70 text-xs">{o.customer_email}</div>
              <div className="text-ardoise/70 text-xs">{o.customer_phone}</div>
            </div>
            <div className="border border-dore/10 p-3">
              <div className="text-[11px] uppercase tracking-wider text-ardoise/60 mb-1 flex items-center gap-1"><MapPin size={12} /> Livraison</div>
              <div className="text-encre text-xs">{o.shipping?.address}</div>
              <div className="text-ardoise/70 text-xs">{o.shipping?.zip} {o.shipping?.city}</div>
              <div className="text-ardoise/70 text-xs">{o.shipping?.state}</div>
            </div>
          </div>

          {/* Articles */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-ardoise/60 mb-2">Articles</div>
            {o.items.map((it, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-dore/10">
                {it.image && <img src={it.image} alt="" className="w-10 h-12 object-cover" referrerPolicy="no-referrer" />}
                <div className="flex-1 text-xs">
                  <div className="text-encre">{it.name}</div>
                  <div className="text-ardoise/60">{[it.size, it.color].filter(Boolean).join(" · ")} · SKU {it.sku}</div>
                </div>
                <div className="text-xs text-encre">{it.quantity} × {eur(it.unit_price)}</div>
              </div>
            ))}
            <div className="text-right text-sm text-encre mt-2">Total : <b>{eur(o.total_amount)}</b></div>
          </div>

          {/* Emails */}
          {o.emails?.length > 0 && (
            <div className="text-xs" data-testid="order-emails">
              <div className="text-[11px] uppercase tracking-wider text-ardoise/60 mb-1 flex items-center gap-1"><Mail size={12} /> Emails</div>
              {o.emails.map((e, i) => <div key={i} className="text-ardoise/70">📧 {e.type} → {e.to} {e.simulated && <span className="text-rose bg-rose/20 px-1">[Simulé]</span>}</div>)}
            </div>
          )}

          {/* Historique */}
          <div className="text-xs">
            <div className="text-[11px] uppercase tracking-wider text-ardoise/60 mb-1 flex items-center gap-1"><Clock size={12} /> Historique</div>
            {o.status_history?.map((h, i) => <div key={i} className="text-ardoise/70">{fmt(h.at)} — <Badge s={h.status} /> {h.note}</div>)}
          </div>

          {/* Actions */}
          <div className="grid grid-cols-2 gap-2 pt-2">
            {(o.status === "ON_HOLD" || o.status === "PAID") && (
              <button onClick={() => act(() => orderResend(id), "resend")} disabled={busy} data-testid="detail-resend" className="flex items-center justify-center gap-1.5 bg-noir text-white py-2 text-[11px] uppercase tracking-wider hover:bg-encre disabled:opacity-50">
                {busy === "resend" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Renvoyer à CJ
              </button>
            )}
            {TIMELINE.includes(o.status) && o.status !== "DELIVERED" && (
              <button onClick={() => act(() => orderAdvance(id), "advance")} disabled={busy} data-testid="detail-advance" className="flex items-center justify-center gap-1.5 border border-dore/30 py-2 text-[11px] uppercase tracking-wider hover:bg-dore/10 disabled:opacity-50">
                {busy === "advance" ? <Loader2 size={13} className="animate-spin" /> : <ChevronRight size={13} />} Avancer le suivi
              </button>
            )}
            {o.status !== "DELIVERED" && o.status !== "REFUNDED" && (
              <button onClick={() => act(() => orderMarkDelivered(id), "deliver")} disabled={busy} data-testid="detail-delivered" className="flex items-center justify-center gap-1.5 border border-green-300 text-green-700 py-2 text-[11px] uppercase tracking-wider hover:bg-green-50 disabled:opacity-50">
                <CheckCircle2 size={13} /> Marquer livrée
              </button>
            )}
            {o.status !== "REFUNDED" && (
              <button onClick={() => act(() => orderCancel(id, "Annulation admin"), "cancel")} disabled={busy} data-testid="detail-cancel" className="flex items-center justify-center gap-1.5 border border-red-300 text-red-600 py-2 text-[11px] uppercase tracking-wider hover:bg-red-50 disabled:opacity-50">
                <Ban size={13} /> Annuler / Rembourser
              </button>
            )}
          </div>

          {/* Note */}
          <div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note interne..." data-testid="detail-note" className="w-full border border-dore/20 p-2 text-xs" rows={2} />
            <button onClick={() => act(() => orderNote(id, note), "note")} className="text-[11px] text-dore hover:underline mt-1">Enregistrer la note</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([ordersList({ status: status || undefined, q: q || undefined }), ordersStats()]);
      setOrders(l.orders); setStats(s);
    } catch (e) { toast.error(errMessage(e)); }
  }, [status, q]);

  useEffect(() => { load(); }, [load]);

  const simulate = async (scenario) => {
    setBusy("sim");
    try { const r = await orderSimulate(scenario); toast.success(`Commande simulée : ${r.order.order_number}`); load(); }
    catch (e) { toast.error(errMessage(e, "Simulation impossible")); }
    setBusy("");
  };
  const refresh = async () => { setBusy("refresh"); try { const r = await ordersRefreshTracking(); toast.success(`Suivi mis à jour (${r.advanced})`); load(); } catch (e) { toast.error(errMessage(e)); } setBusy(""); };

  return (
    <AdminShell title="Commandes">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <Stat label="Commandes totales" value={stats.total} Icon={ShoppingBag} tid="stat-total" />
          <Stat label="CA (24h)" value={eur(stats.revenue_today)} Icon={Euro} tid="stat-revenue" />
          <Stat label="À expédier / en cours" value={stats.shipped} Icon={Truck} tid="stat-shipped" />
          <Stat label="En attente (problème)" value={stats.on_hold} Icon={AlertTriangle} tid="stat-onhold" />
        </div>
      )}

      {/* Simulation banner */}
      <div className="border border-dore/20 bg-rose/10 p-4 mb-5" data-testid="simulate-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-encre flex items-center gap-2"><PlayCircle size={16} className="text-dore" /> <b>Simuler une commande</b> <span className="text-ardoise/60 text-xs">(test sans Stripe réel — MODE MOCK)</span></div>
          <div className="flex gap-2">
            <button onClick={() => simulate("success")} disabled={busy} data-testid="sim-success" className="bg-noir text-white px-3 py-1.5 text-[11px] uppercase tracking-wider hover:bg-encre disabled:opacity-50">{busy === "sim" ? <Loader2 size={13} className="animate-spin" /> : "Succès"}</button>
            <button onClick={() => simulate("cj_fail")} disabled={busy} data-testid="sim-fail" className="border border-red-300 text-red-600 px-3 py-1.5 text-[11px] uppercase tracking-wider hover:bg-red-50 disabled:opacity-50">Échec CJ</button>
            <button onClick={() => simulate("out_of_stock")} disabled={busy} data-testid="sim-oos" className="border border-amber-300 text-amber-600 px-3 py-1.5 text-[11px] uppercase tracking-wider hover:bg-amber-50 disabled:opacity-50">Rupture</button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-2.5 text-ardoise/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (n°, client, tracking...)" data-testid="orders-search" className="w-full border border-dore/20 pl-9 pr-3 py-2 text-sm" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="orders-filter-status" className="border border-dore/20 px-3 py-2 text-sm">
          <option value="">Tous les statuts</option>
          {Object.keys(STATUS).map((k) => <option key={k} value={k}>{STATUS[k].label}</option>)}
        </select>
        <button onClick={refresh} disabled={busy} data-testid="orders-refresh" className="flex items-center gap-1.5 border border-dore/30 px-3 py-2 text-[11px] uppercase tracking-wider hover:bg-dore/10 disabled:opacity-50">
          {busy === "refresh" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Rafraîchir suivi
        </button>
      </div>

      {/* List */}
      <div className="border border-dore/20 bg-white divide-y divide-dore/10" data-testid="orders-list">
        {orders.length === 0 ? <p className="p-6 text-sm text-ardoise/60 text-center" data-testid="orders-empty">Aucune commande. Utilisez « Simuler une commande » pour tester le workflow.</p> :
          orders.map((o) => (
            <button key={o.id} onClick={() => setSelected(o.id)} data-testid="order-row" className="w-full flex items-center justify-between px-4 py-3 hover:bg-rose/5 text-left transition-colors">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-encre font-medium">{o.order_number}</span>
                  <Badge s={o.status} />
                </div>
                <div className="text-xs text-ardoise/60 mt-0.5">{o.customer_name} · {fmt(o.created_at)} {o.tracking_number && `· 🚚 ${o.tracking_number}`}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-encre">{eur(o.total_amount)}</span>
                <ChevronRight size={16} className="text-ardoise/40" />
              </div>
            </button>
          ))}
      </div>

      {selected && <OrderDetail id={selected} onClose={() => setSelected(null)} onChange={load} />}
    </AdminShell>
  );
}
